/**
 * Client-side RTC diagnostics recorder. One logger per call/meeting session:
 * buffers events (state transitions, ICE candidates, stats snapshots,
 * transport/token events from the diagnostics bus) and ships them in batches
 * to `POST /realtime/diagnostics/` — with a `fetch keepalive` final flush on
 * pagehide so "the user closed the tab" is distinguishable from a real drop.
 *
 * Console mirror: set `localStorage["elg5:rtcDebug"] = "1"` to see every
 * event and 10-second stats sample in the devtools console.
 */

import apiClient, { getLiveAccessToken } from "@/api/client";
import { subscribeRtcDiagnostics } from "./diagnosticsBus";
import { collectStatsSnapshot, parseCandidateType } from "./rtcStats";

export type RtcSessionKind = "screenshare" | "meeting" | "transport";

export interface RtcLoggedEvent {
  session_kind: RtcSessionKind;
  session_id: string;
  event_type: string;
  peer_id?: string;
  ts: number;
  payload?: Record<string, unknown>;
}

export interface RtcLogger {
  log: (
    type: string,
    payload?: Record<string, unknown>,
    peerId?: string,
  ) => void;
  /** Wire state/candidate/stats listeners onto a peer connection. Returns a detach fn. */
  attachPc: (pc: RTCPeerConnection, peerId?: string) => () => void;
  flush: () => void;
  dispose: () => void;
}

export interface RtcLoggerOptions {
  kind: RtcSessionKind;
  sessionId: string;
  peerId?: string;
  /** Override the transport — meeting guests post via their token-scoped endpoint. */
  send?: (events: RtcLoggedEvent[]) => Promise<void>;
  /** Override the pagehide flush (must use fetch keepalive / sendBeacon semantics). */
  sendKeepalive?: (events: RtcLoggedEvent[]) => void;
  /** Which events get persisted (default: all). Rejected ones still reach the live
   *  dev panel — they are only kept off the server. */
  persist?: (type: string, payload?: Record<string, unknown>) => boolean;
  /** Periodic flush of the buffer (default FLUSH_INTERVAL_MS). */
  flushIntervalMs?: number;
}

// ── live tap ─────────────────────────────────────────────────────────────────
// In-app subscribers (the developer log panel in the meeting room) get every
// event the moment it is logged — including the 10-second stats samples that
// are only persisted every third time. Purely observational: listeners cannot
// affect buffering/shipping, and a throwing listener is ignored.

type LiveListener = (event: RtcLoggedEvent) => void;
const liveListeners = new Set<LiveListener>();

/** Subscribe to the live RTC event stream. Returns an unsubscribe fn. */
export function subscribeLiveRtcLog(listener: LiveListener): () => void {
  liveListeners.add(listener);
  return () => liveListeners.delete(listener);
}

function emitLive(event: RtcLoggedEvent): void {
  for (const listener of liveListeners) {
    try {
      listener(event);
    } catch {
      /* a broken panel must never disturb a live call */
    }
  }
}

const MAX_BUFFER = 200;
const FLUSH_INTERVAL_MS = 15_000;
const FLUSH_AT = 50;
const MAX_BATCH = 100; // mirrors RtcDiagnosticsIngestView.MAX_BATCH
const STATS_SAMPLE_MS = 10_000;
const PERSIST_EVERY_NTH_SAMPLE = 3; // console every 10 s, persisted every 30 s

// ── send pacing (shared by every logger in this tab) ─────────────────────────
// The ingest endpoint is throttled PER USER (`rtc-diagnostics` in DRF settings),
// so every open tab of one person drains the same bucket. A Centrifugo
// reconnect storm (deploy, flapping websocket) emits ~2 events per channel per
// reconnect in each tab — with a few dozen rooms that is a 50-event batch per
// reconnect per tab, and the threshold flush used to POST each one the instant
// it filled. Several tabs × several reconnects a minute blew through the bucket
// and every later batch came back 429 ("Żądanie zostało zdławione").
//
// Two brakes, both tab-wide (one bucket ↔ one pacing state):
//  - at most one POST per MIN_SEND_GAP_MS; a burst waits in the buffer (bounded
//    by MAX_BUFFER — overflow drops the OLDEST events and is reported in the
//    next batch as a `diagnostics_dropped` marker so the gap is visible);
//  - a 429 parks sending for the server's Retry-After (clamped), because
//    re-posting into a full bucket only earns another 429.
// `dispose()` / pagehide bypass the gap (last chance) but never the 429 park.
const MIN_SEND_GAP_MS = 2_000;
const THROTTLE_PAUSE_DEFAULT_MS = 15_000;
const THROTTLE_PAUSE_MIN_MS = 5_000;
const THROTTLE_PAUSE_MAX_MS = 120_000;
const DROPPED_MARKER_EVENT = "diagnostics_dropped";

let lastSendAt = 0;
let pausedUntil = 0;

/** Test hook: forget tab-wide pacing/backoff state between test cases. */
export function resetRtcSendPacing(): void {
  lastSendAt = 0;
  pausedUntil = 0;
}

function httpStatus(err: unknown): number | undefined {
  const e = err as { response?: { status?: number }; status?: number } | null;
  return e?.response?.status ?? e?.status;
}

/** Retry-After of a throttled response in ms, clamped; default when absent. */
function throttlePauseMs(err: unknown): number {
  const headers = (err as { response?: { headers?: Record<string, unknown> } })
    ?.response?.headers;
  const raw = headers?.["retry-after"] ?? headers?.["Retry-After"];
  const seconds = Number(raw);
  const asked =
    Number.isFinite(seconds) && seconds > 0
      ? seconds * 1000
      : THROTTLE_PAUSE_DEFAULT_MS;
  // Jitter so every tab of the same user does not resume in the same instant.
  const jitter = 1 + Math.random() * 0.5;
  return Math.min(
    Math.max(asked * jitter, THROTTLE_PAUSE_MIN_MS),
    THROTTLE_PAUSE_MAX_MS,
  );
}

/** Note a throttled send: park every logger in this tab for the asked time. */
function noteSendFailure(err: unknown): void {
  if (httpStatus(err) !== 429) return;
  pausedUntil = Math.max(pausedUntil, Date.now() + throttlePauseMs(err));
}

/** ms until the next POST is allowed (0 = now). */
function sendWaitMs(now: number, { ignoreGap }: { ignoreGap: boolean }): number {
  const gapReady = ignoreGap ? 0 : lastSendAt + MIN_SEND_GAP_MS;
  return Math.max(0, Math.max(pausedUntil, gapReady) - now);
}

function debugEnabled(): boolean {
  try {
    return localStorage.getItem("elg5:rtcDebug") === "1";
  } catch {
    return false;
  }
}

function defaultSend(events: RtcLoggedEvent[]): Promise<void> {
  return apiClient
    .post("/realtime/diagnostics/", { events })
    .then(() => undefined);
}

function defaultSendKeepalive(events: RtcLoggedEvent[]): void {
  // sendBeacon cannot carry the Authorization header — keepalive fetch can.
  // There is no time to refresh on pagehide, so a token known to be expired is
  // not sent at all: the request could only come back as a 401.
  const base = apiClient.defaults.baseURL ?? "/api/v1";
  const token = getLiveAccessToken();
  if (!token) return;
  void fetch(`${base}/realtime/diagnostics/`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ events }),
  }).catch(() => {});
}

export function createRtcLogger(options: RtcLoggerOptions): RtcLogger {
  const send = options.send ?? defaultSend;
  const sendKeepalive = options.sendKeepalive ?? defaultSendKeepalive;

  let buffer: RtcLoggedEvent[] = [];
  let disposed = false;
  let dropped = 0;
  let flushTimer: ReturnType<typeof setTimeout> | null = null;
  const detachFns: Array<() => void> = [];

  const droppedMarker = (): RtcLoggedEvent => ({
    session_kind: options.kind,
    session_id: options.sessionId,
    event_type: DROPPED_MARKER_EVENT,
    peer_id: options.peerId,
    ts: Date.now(),
    payload: { count: dropped },
  });

  const takeBatch = (): RtcLoggedEvent[] => {
    // The marker rides along with the batch (never buffered — it must not be
    // the thing that overflows the buffer) and takes one of its MAX_BATCH slots.
    if (dropped > 0) {
      const batch = [droppedMarker(), ...buffer.splice(0, MAX_BATCH - 1)];
      dropped = 0;
      return batch;
    }
    return buffer.splice(0, MAX_BATCH);
  };

  const clearFlushTimer = () => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  };

  const scheduleFlush = (delayMs: number) => {
    if (flushTimer || disposed) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flush();
    }, delayMs);
  };

  /** Send what the pacing allows now; park the rest on a timer. */
  const flush = (opts: { ignoreGap: boolean } = { ignoreGap: false }) => {
    if (buffer.length === 0 && dropped === 0) return;
    const wait = sendWaitMs(Date.now(), opts);
    if (wait > 0) {
      scheduleFlush(wait);
      return;
    }
    clearFlushTimer();
    const batch = takeBatch();
    lastSendAt = Date.now();
    void send(batch).catch((err: unknown) => {
      // Diagnostics are best-effort — never disturb a live call. A 429 only
      // parks further sends so the bucket can drain.
      noteSendFailure(err);
    });
    if (buffer.length > 0) scheduleFlush(sendWaitMs(Date.now(), opts) || MIN_SEND_GAP_MS);
  };

  const flushKeepalive = () => {
    // The page is going away: no pacing (there is no "later"), but a bucket
    // the server just declared full is not worth another POST.
    if (sendWaitMs(Date.now(), { ignoreGap: true }) > 0) {
      buffer = [];
      dropped = 0;
      return;
    }
    while (buffer.length > 0 || dropped > 0) {
      try {
        sendKeepalive(takeBatch());
      } catch {
        buffer = [];
        dropped = 0;
      }
    }
  };

  const log = (
    type: string,
    payload?: Record<string, unknown>,
    peerId?: string,
  ) => {
    if (disposed) return;
    if (debugEnabled()) {
      console.debug(`[rtc:${options.kind}]`, type, payload ?? "", peerId ?? "");
    }
    const event: RtcLoggedEvent = {
      session_kind: options.kind,
      session_id: options.sessionId,
      event_type: type,
      peer_id: peerId ?? options.peerId,
      ts: Date.now(),
      payload,
    };
    emitLive(event);
    if (options.persist && !options.persist(type, payload)) return;
    buffer.push(event);
    if (buffer.length > MAX_BUFFER) {
      const overflow = buffer.length - MAX_BUFFER;
      buffer.splice(0, overflow);
      dropped += overflow;
    }
    if (buffer.length >= FLUSH_AT) flush();
  };

  const interval = setInterval(() => flush(), options.flushIntervalMs ?? FLUSH_INTERVAL_MS);
  detachFns.push(() => clearInterval(interval));

  // Transport-level events (Centrifugo states, token refreshes) emitted by
  // chat/api/realtime.ts while this session is alive.
  detachFns.push(subscribeRtcDiagnostics((e) => log(e.type, e.data)));

  const onPageHide = () => {
    log("page", { event: "pagehide" });
    flushKeepalive();
  };
  const onVisibility = () => {
    log("page", { event: "visibility", state: document.visibilityState });
  };
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);
  detachFns.push(() => {
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibility);
  });

  const attachPc = (pc: RTCPeerConnection, peerId?: string): (() => void) => {
    const pcDetach: Array<() => void> = [];
    const on = <K extends keyof RTCPeerConnectionEventMap>(
      name: K,
      handler: (event: RTCPeerConnectionEventMap[K]) => void,
    ) => {
      pc.addEventListener(name, handler as EventListener);
      pcDetach.push(() => pc.removeEventListener(name, handler as EventListener));
    };

    on("connectionstatechange", () =>
      log("pc_state", { state: pc.connectionState }, peerId),
    );
    on("iceconnectionstatechange", () =>
      log("ice_state", { state: pc.iceConnectionState }, peerId),
    );
    on("icegatheringstatechange", () =>
      log("ice_gathering", { state: pc.iceGatheringState }, peerId),
    );
    on("signalingstatechange", () =>
      log("signaling_state", { state: pc.signalingState }, peerId),
    );
    on("icecandidate", (e) => {
      if (!e.candidate) return;
      // Candidate TYPE only (host/srflx/relay) — never the address.
      log(
        "ice_candidate",
        {
          candidate_type: parseCandidateType(e.candidate.candidate),
          protocol: e.candidate.protocol ?? undefined,
        },
        peerId,
      );
    });
    on("icecandidateerror", (e) => {
      const err = e as RTCPeerConnectionIceErrorEvent;
      log(
        "ice_candidate_error",
        { code: err.errorCode, text: err.errorText, url: err.url },
        peerId,
      );
    });

    let sampleCount = 0;
    const statsTimer = setInterval(() => {
      void collectStatsSnapshot(pc).then((snapshot) => {
        if (!snapshot) return;
        sampleCount += 1;
        if (debugEnabled()) {
          console.debug(`[rtc:${options.kind}] stats`, peerId ?? "", snapshot);
        }
        // Persist the FIRST sample (10 s) and every third after: real failed
        // calls are short — the 2026-08-04 attempts lasted 15-20 s and ended
        // before the old 30 s first-persist ever fired.
        if (sampleCount % PERSIST_EVERY_NTH_SAMPLE === 1) {
          log("stats_snapshot", snapshot as Record<string, unknown>, peerId);
        } else if (!disposed) {
          // Off-cycle samples are live-only: the dev panel sees fresh numbers
          // every 10 s without tripling what gets persisted.
          emitLive({
            session_kind: options.kind,
            session_id: options.sessionId,
            event_type: "stats_sample",
            peer_id: peerId ?? options.peerId,
            ts: Date.now(),
            payload: snapshot as Record<string, unknown>,
          });
        }
      });
    }, STATS_SAMPLE_MS);
    pcDetach.push(() => clearInterval(statsTimer));

    const detach = () => {
      for (const fn of pcDetach.splice(0)) fn();
    };
    detachFns.push(detach);
    return detach;
  };

  const dispose = () => {
    if (disposed) return;
    clearFlushTimer();
    // During pagehide a normal XHR may be killed mid-flight — use keepalive.
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      flushKeepalive();
    } else {
      // Last chance for this session's buffer: skip the pacing gap (a couple
      // of extra POSTs at teardown is fine), keep honouring a 429 park.
      while ((buffer.length > 0 || dropped > 0) && sendWaitMs(Date.now(), { ignoreGap: true }) === 0) {
        flush({ ignoreGap: true });
      }
      buffer = [];
      dropped = 0;
    }
    disposed = true;
    for (const fn of detachFns.splice(0)) fn();
  };

  return { log, attachPc, flush: () => flush(), dispose };
}
